/**
 * 정책 API 공용 토대 — 요청 껍데기(fetchJson·인증 헤더·ETag), 공통 어휘(Carrier·DealType),
 * 캐시 무효화. ⚠️ 이 파일은 policyApi/ 안의 다른 모듈을 import 하지 않는다(의존 최하층).
 *
 * ⚠️ src/pages/Policy/policyApi.ts 를 도메인별로 분해한 조각이다(2026-08-30).
 *    파일 이름은 백엔드 server/src/v2/routes/policy/ 와 짝을 맞췄다.
 */
import type { QueryClient } from "@tanstack/react-query";
import { apiError } from "../../../api/_error";
import {
  authJson,
  authForm,
  authFetch,
  buildAuthHeaders,
} from "../../../api/_client";
import type { PolicyGroupSyncReview } from "../../../../shared/policy-model-group-verbal-sync";
import type { VerbalRememberedAmount } from "../../../../shared/policy-verbal-remembered-amounts";

export const API = `${import.meta.env.VITE_API_BASE_URL ?? ""}/api/v2/policy`;

/** 정책 파이프라인 관련 모든 쿼리를 한 번에 무효화 — 업로드·확정·삭제 후 파이프라인·시세표·내역·상세가
 *  화면 어디에 있든 즉시 다시 불러오게 한다. 정책 쿼리 키는 전부 ["policy", ...] 로 시작하므로
 *  접두어(prefix) 무효화로 board·cells·calendar·plan-rebate·pipeline·mappings·plan-buckets 등
 *  가격·합산에 직결되는 쿼리를 빠짐없이 휩쓴다 (React Query 의 부분 일치 동작).
 *
 *  단 하나의 예외 — 전역 모델 카탈로그(["policy","models", ...], fetchModels): 모델 마스터는
 *  seed 로 적재되는 표시용(코드·이름·제조사·펫네임) 데이터라 정책 업로드·확정·삭제로는 바뀌지 않고,
 *  가격(출고가·리베·공시)을 전혀 담지 않는다(staleTime 5분 의도). 모델 이름 편집은 별도 ["catalog", ...]
 *  네임스페이스에서 자기 키를 직접 무효화한다(PolicyCatalogPage). 그래서 카탈로그만 무효화 대상에서 빼
 *  거의 안 바뀌는 캐시가 정책 변경마다 통째로 다시 구워지는 낭비를 막는다 — 가격 정확성에는 영향 없음.
 *  (표준 구간 plan-buckets 는 PolicyPlanGroupManageModal 이 이 함수로 갱신을 의존하므로 그대로 둔다.) */
export function invalidatePolicyQueries(qc: QueryClient): Promise<void> {
  return qc.invalidateQueries({
    queryKey: ["policy"],
    // 모델 카탈로그(["policy","models", ...])만 제외 — 그 외 모든 ["policy", ...] 군은 무효화.
    predicate: (q) => q.queryKey[1] !== "models",
  });
}

// 만원 금액 표시(formatMan)는 lib/format.ts 로 일원화됨 — 거기서 import 한다.

// 인증 헤더만 — 공통 buildAuthHeaders 로 위임 (uploadFaceFile 등 multipart 직접 fetch 에서 사용).
export function authHeaders(init?: RequestInit): Promise<Headers> {
  return buildAuthHeaders(init);
}

// JSON 요청 — body 가 string 일 때만 Content-Type=application/json (FormData 등은 건드리지 않음).
export function fetchJson<T>(url: string, init?: RequestInit, timeoutMs?: number): Promise<T> {
  return authJson<T>(url, init, "string-only", timeoutMs);
}

/**
 * 원본 재파싱을 동반하는 저장(별칭 지정 → 그 자리에서 표 다시 만들기)에 쓰는 상한.
 *
 * 서버는 `/mappings/:id/reprocess`·`/mappings/batch` 를 **설계상 오래 걸리는 정상 요청**으로
 * 분류해 165초까지 허용한다(server/src/lib/request-timeout.ts LONG_REQUEST_PATTERNS —
 * 엑셀 원본을 통째로 다시 읽고 정책 heavy lane 을 탄다). 그런데 클라이언트 기본은 15초라,
 * 서버가 정상적으로 일하는 동안 화면만 먼저 포기하고 "15초를 넘겨 요청을 멈췄어요 / 저장됐는지
 * 알 수 없어요" 를 띄웠다(2026-08-17, 표준 모델 지정 모달).
 *
 * 그래서 **서버 상한보다 크게** 잡는다(165초 + 여유 5초). 끊는 판단은 서버 미들웨어가 하게 둬야
 * 사용자가 그쪽의 제대로 된 안내(작업이 계속 돌 수 있다 · retrySafe 계약)를 받는다 —
 * 클라이언트가 먼저 끊으면 그 장치가 한 번도 닿지 않는다. 시세표 조회가 35초를 쓰는 이유와
 * 같은 규칙이다(fetchJsonEtag).
 */
export const REPROCESS_TIMEOUT_MS = 170_000;

// ── ETag 조건부 GET (pipeline·board·시세표) ───────────────────────
// 서버가 정책·카탈로그 버전 기반 ETag 를 발급한다. 직전 ETag 를 If-None-Match 로 되보내,
// 데이터가 안 바뀌었으면 304(본문 0)를 받아 직전 본문을 그대로 재사용한다 — 재검증(staleTime
// 만료·탭 복귀)마다 대용량(board 1MB+)을 다시 전송·파싱하던 비용을 없앤다. 전역 fetch 는
// 건드리지 않고 이 세 함수에만 적용해 다른 API 회귀를 막는다(authFetch=raw Response·401 재시도 재사용).
export const etagCache = new Map<string, { etag: string; body: unknown }>();

export const ETAG_CACHE_MAX = 32; // url(날짜×범위×detail×통신사) 종류 상한 — 넘으면 가장 오래된 것부터 축출

/** 로그아웃·매장 전환 시 호출 — 프론트 ETag 메모리 캐시를 비운다(매장 간 본문 잔존 차단).
 *  서버 ETag 에 이미 tenantId 가 박혀 매장이 다르면 304 가 안 나지만, 메모리에 옛 본문을
 *  들고 있을 이유가 없으므로 함께 비운다(이중 방어). */
export function clearPolicyEtagCache(): void {
  etagCache.clear();
  gridEtagCache.clear();
}

/** 원본 엑셀 격자(업로드 1건당 수 MB) 전용 — 시세표·board 본문을 밀어내지 않게 따로, 작게 둔다. */
export const gridEtagCache = new Map<string, { etag: string; body: unknown }>();
export const GRID_ETAG_CACHE_MAX = 4;

export async function fetchJsonEtag<T>(
  url: string,
  signal?: AbortSignal,
  store: { cache: Map<string, { etag: string; body: unknown }>; max: number } = { cache: etagCache, max: ETAG_CACHE_MAX },
): Promise<T> {
  const cached = store.cache.get(url);
  const init: RequestInit | undefined =
    cached || signal
      ? {
          ...(cached ? { headers: { "If-None-Match": cached.etag } } : undefined),
          signal,
        }
      : undefined;
  // 시세표 셀·board 는 콜드 캐시일 때 서버가 최대 heavy-compute 상한(30s)까지 계산할 수 있다.
  // 클라이언트 타임아웃이 그보다 짧으면(옛 15s) 서버가 끝내 캐시를 채우기도 전에 사용자가 먼저
  // 실패해 "최신 시세표 반영 중" 배너만 뜨다 사라진다 → 서버 상한보다 크게(35s) 둔다.
  const res = await authFetch(url, init, 35000);
  if (res.status === 304 && cached) return cached.body as T;
  if (!res.ok) throw await apiError(res);
  const body = (await res.json()) as T;
  const etag = res.headers.get("ETag");
  if (etag) {
    store.cache.delete(url); // 다시 넣어 삽입순 맨 뒤(최근)로
    if (store.cache.size >= store.max) {
      const oldest = store.cache.keys().next().value; // Map 삽입순 → 가장 오래된 것
      if (oldest !== undefined) store.cache.delete(oldest);
    }
    store.cache.set(url, { etag, body });
  }
  return body;
}

/** multipart 전송 — Content-Type은 브라우저가 boundary 포함해 직접 설정 */
export function fetchJsonForm<T>(url: string, form: FormData, timeoutMs?: number): Promise<T> {
  return authForm<T>(url, form, undefined, timeoutMs);
}

// ───────────────────── 타입 (CONTRACT.md DTO) ─────────────────────

export type Carrier = "SK" | "KT" | "LG";

export type DealType = "신규" | "MNP" | "기변";

/** 가입유형을 사람에게 보일 때의 고정 순서 — 구두 표·엑셀이 같은 순서로 읽히게 하는 표시 규칙.
 *  (구두 관리 모달과 그 엑셀 내보내기가 공유한다 — 한쪽만 바꾸면 두 화면이 어긋난다.) */
export const DEAL_ORDER = ["전체", "신규", "MNP", "기변"];

export function uniq<T>(values: T[]): T[] {
  return [...new Set(values)];
}

/** 업로드 1건의 처리 상태.
 *  - confirmed: 합산 반영됨. 엑셀 액면은 업로드 즉시 confirmed(확정 게이트 폐지).
 *  - vendor-pending: 업체를 못 알아낸 액면 — 사람이 업체 지정해야 confirmed. 합산 제외.
 *  - review-pending: 이미지 단가표를 파싱한 직후 — 사람이 확정 전이라 합산 제외(2026-06-23). 검토 화면에서 확정한다.
 *  - processing: 접수만 됐고 파싱은 서버 뒤편에서 도는 중 — 합산 제외. 화면이 진행을 폴링해 저절로 갱신한다.
 *  - failed: 처리가 끝내 실패 — 사유(failureReason)를 보여주고 다시 올리게 한다.
 *  - canceled: 사람이 취소 버튼을 눌러 버린 업로드 — 아무것도 반영되지 않는다(부분 반영 없음). */
export type UploadStatus =
  | "vendor-pending"
  | "review-pending"
  | "confirmed"
  | "processing"
  | "failed"
  | "canceled";

/** 표준화된 정책 행 (StandardRowDraft & { id }) */
export type PolicyRow = {
  id: number;
  modelRaw: string;
  modelStd: string | null; // null = 미매핑
  modelName: string | null;
  /** 자동 추론값이라 사람이 뜻을 확정하기 전까지 금액에서 제외되는 표기 */
  modelMappingRequired?: boolean;
  modelMappingReason?: "unmapped" | "inferred" | "ambiguous" | null;
  planRaw: string;
  planStd: string | null; // null = 미매핑
  dealType: DealType | null; // null = 해석 불가 원문
  dealTypeRaw: string;
  /** 약정유형(2026-09-03) — 공시(기본)·선약·공통. 옛 응답엔 없을 수 있어 선택값(없으면 공시로 본다). */
  contractType?: "공시" | "선약" | "공통";
  amount: number; // 만원
  release: number | null; // 원
  cell: string;
};

/** 구두 초안 항목 — LLM 구조화 결과, 사람이 수정 후 확정 */
/** 유지성/스팟성 색 견출지 (티켓 #58). null = 미지정(색 없음) */
export type VerbalTag = "keep" | "spot" | null;

/** 색 견출지 순환 — 누를 때마다 색 없음 → 유지(파랑) → 스팟(빨강) → 색 없음 */
export const VERBAL_TAG_CYCLE: VerbalTag[] = [null, "keep", "spot"];

export const nextVerbalTag = (cur: VerbalTag): VerbalTag =>
  VERBAL_TAG_CYCLE[(VERBAL_TAG_CYCLE.indexOf(cur ?? null) + 1) % VERBAL_TAG_CYCLE.length];

/**
 * 구두 표시 순서 SSOT (티켓 #58) — 서버 verbalOrderSql/compareVerbalOrder 와 같은 규칙.
 * 사람이 정한 자리(sortOrder)가 먼저, 아직 안 정한 구두(null)는 뒤에서 등록순(uploadedAt, id).
 * 덕분에 새로 등록한 구두는 위에 고정해 둔 구두들을 밀어내지 않고 맨 아래에 붙는다.
 */
export function compareVerbalOrder(
  a: { sortOrder?: number | null; uploadedAt: string; id: number },
  b: { sortOrder?: number | null; uploadedAt: string; id: number },
) {
  return (
    (a.sortOrder ?? Number.MAX_SAFE_INTEGER) - (b.sortOrder ?? Number.MAX_SAFE_INTEGER) ||
    a.uploadedAt.localeCompare(b.uploadedAt) ||
    a.id - b.id
  );
}

export type VerbalItem = {
  id?: number; // 신규 추가 행은 id 없음 (서버가 부여)
  modelRaw: string;
  modelStd: string | null;
  petName?: string | null; // 통신사 마케팅명 — 서버가 (모델코드,통신사)로 붙여줌. 표시 전용(저장 시 안 보냄)
  planRange: string; // 예: "SK_109 이상"
  dealType: DealType | "전체";
  amount: number; // 추가 리베이트 (만원)
};

export type VerbalEntryDto = {
  id: number;
  vendorId: number | null; // 검토 그리드가 이 업체의 "구분" 그룹만 불러오는 데 쓴다
  vendor: string | null;
  name: string | null; // 사람이 붙인 정책 별칭 — "구두N · 이름"으로 표시. null = 미입력(번호만)
  /** 부가 정보 4종 — 구두 관리 표에서 인라인 입력하는 사람용 메모(합산 계산엔 안 쓰임). null = 미입력 */
  conditionText: string | null; // 조건 (자유 텍스트)
  contractType: string | null; // 약정유형 — '공시'|'선약'|'공통'
  retention: string | null; // 유지기간 — "d+3"·"m+3" 같은 자유 표기
  memo: string | null; // 기타메모
  /** 모델이 없어도 먼저 고른 가입유형·요금제조건(티켓 #389). */
  preselectedDealTypes: string[];
  preselectedPlanRanges: string[];
  /** 모델을 비운 동안 보관하는 요금제 구간×가입유형×금액 한 벌. */
  rememberedAmounts: VerbalRememberedAmount[];
  /** 해당모델 칸에서 그룹과 별개로 직접 고른 표준 모델 코드(티켓 #390). */
  directModelCodes: string[];
  /** 해당모델 칸에 **사람이 넣은 그룹 키**(`vendor:<id>`/`global:<id>`) — 칸에 보일 그룹 이름의
   *  진본이다(티켓 #483). 비어 있으면(옛 구두) 종전대로 모델 구성으로 되짚어 보여준다. */
  modelGroupKeys: string[];
  /** 모델 그룹 자동 반영 뒤 금액 확인이 남았으면 초록 리베이트 칸의 근거. */
  groupSyncReview: PolicyGroupSyncReview | null;
  carrier: Carrier | null; // 업체 통신사 — 구간 열 셀렉트가 이 통신사 구간만 보여준다
  sourceType: "text" | "image";
  sourceText: string;
  periodFrom: string | null;
  periodTo: string | null;
  periodFromTime: string | null;
  periodToTime: string | null;
  uploadedAt: string; // ISO
  /** 구두 관리 표에서 사람이 드래그로 정한 자리. null = 아직 안 정함(등록순으로 맨 뒤) — 티켓 #58 */
  sortOrder: number | null;
  /** 유지성(keep, 파랑)/스팟성(spot, 빨강) 색 견출지. null = 색 없음. 보기용이라 합산 계산엔 안 쓰인다 */
  tag: VerbalTag;
  status: UploadStatus;
  /** 활성 여부 — false 면 시세표·파이프라인 합산에서 빠진다(데이터는 유지·되돌릴 수 있음) */
  active: boolean;
  /** 이미지 구두의 원본 사진 주소(presigned/data URL) — 단건 detail 에만 실림(파이프라인 묶음엔 없음) */
  sourceImage?: string | null;
  items: VerbalItem[];
  /**
   * 이 구두에 달린 항목의 실제 개수. /pipeline 은 항목을 빼고 이 개수만 싣기 때문에
   * (items 는 빈 배열로 온다) "아직 안 받아온 것"과 "정말 0개"를 이 값으로 가른다.
   * itemCount > 0 인데 items 가 비어 있으면 = 뒤이어 오는 중.
   */
  itemCount?: number;
};

/** 엑셀 한 칸에서 우리가 뽑아 저장한 값 — 호버 툴팁이 그대로 그린다. */
export type ParsedCellInfo = {
  modelRaw: string;
  modelStd: string | null;
  modelName: string | null;
  modelMappingRequired: boolean;
  modelMappingReason: "unmapped" | "inferred" | "ambiguous" | null;
  planRaw: string;
  planStd: string | null;
  /** 구간 코드의 사람이 읽는 이름("109K 이상") — 요금제 헤더 호버가 코드 대신 이걸 보여준다 */
  planLabel: string | null;
  /** 「요금제군 한눈에」에서 사람이 만든 벌의 구간인가 — 그 벌의 코드는 기계용 열쇠(`SK_W_…`)라
   *  호버가 '구간 코드' 줄을 접는다(티켓 #846). 시험 파싱 결과처럼 표준화를 안 거친 자리는 없다. */
  planBucketScoped?: boolean;
  dealType: string | null;
  dealTypeRaw: string;
  /** 약정유형 — 공시·선약·공통. 원본 금액 칸 툴팁에서 요금제와 개통유형 사이에 표시한다. */
  contractType?: "공시" | "선약" | "공통";
  amount: number;
  release: number | null;
  /** 사람이 별칭 관리에서 안 쓰는 표기로 둔 축. 저장된 금액 행은 그대로 두고 화면 판정에만 쓴다. */
  modelIgnored: boolean;
  planIgnored: boolean;
};

/** 감춘 링크를 만들어 한 번 누른다 — 다운로드도, 앱 스킴(ms-excel:)도 같은 방식으로 흘린다. */
export function clickHiddenLink(href: string): void {
  const a = document.createElement("a");
  a.href = href;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** 서명 URL({url})을 받아 브라우저 다운로드로 흘린다 — presigned 에 Content-Disposition: attachment 가
 *  박혀 있어 탭 이동 없이 원래 파일명 그대로 저장된다. 받은 URL 을 돌려준다(엑셀로 이어 열 때 쓴다). */
export async function downloadSigned(endpoint: string): Promise<string> {
  const { url } = await fetchJson<{ url: string }>(endpoint);
  clickHiddenLink(url);
  return url;
}

/**
 * 받은 파일을 **엑셀 창으로도 바로 띄운다** (티켓 #534) — Office URI 스킴.
 *
 * `ms-excel:ofe|u|<주소>` 는 "이 주소를 엑셀로 열어라"라는 뜻이고, 엑셀이 깔린 PC 에서만
 * 처리기가 붙는다. 없는 PC(맥·태블릿·엑셀 미설치)에서는 브라우저가 아무 일도 하지 않는다 —
 * 그래서 **다운로드를 먼저 흘려 두고** 이걸 시도한다. 엑셀이 안 떠도 파일은 이미 받아진 상태라,
 * 최악의 경우가 "예전과 똑같이 받아지기만 하는 것"이 된다.
 *
 * ⚠️ **주소는 반드시 한 겹 감싸서(encodeURIComponent) 넘긴다.** Office URI 처리기는 `u|` 뒤를
 * 넘길 때 퍼센트 인코딩을 **한 번 푼다**. 그런데 우리가 넘기는 건 S3 서명 주소라, 풀리는 순간
 * 서명 재료가 달라져 통째로 무효가 된다 — 특히 `response-content-disposition` 에 한글 파일명이
 * 실려 있어 `%3B%20`·한글이 그대로 드러나면서 주소 형식 자체가 깨진다. 그러면 엑셀이
 * "Sorry, we couldn't open '<주소>'" 오류창을 띄우고, 사용자에겐 **다운로드가 실패한 것처럼**
 * 보인다(2026-08-29 실사고 — 파일은 이미 받아져 있었는데 오류창만 보였다). 한 겹 감싸 두면
 * 처리기가 한 번 푼 결과가 정확히 원래 주소가 된다.
 */
export function openWithExcel(url: string): void {
  clickHiddenLink(`ms-excel:ofe|u|${encodeURIComponent(url)}`);
}

export function verbalDeleteConfirmMessage(name: string | null | undefined): string {
  return `'${name || "이름 없는 구두"}' 을(를) 삭제할까요? 지운 구두는 90일 안에 [지난 구두 가져오기]에서 다시 가져올 수 있습니다.`;
}

/** 전체 리베이트 표에서 여러 표기를 모아 저장한 뒤 원본 업로드를 딱 한 번 다시 읽는다. */
export function batchMapAndReprocess(body: {
  vendorName: string;
  date: string;
  uploadId: number;
  mappings: {
    kind: "model" | "plan";
    raw: string;
    std: string | null;
    expandsTo?: string[] | null;
    globalModelGroupIds?: number[] | null;
  }[];
}): Promise<{ ok: true; uploadId: number; mappingIds: number[]; queued?: boolean; total?: number; uploadIds?: number[] }> {
  return fetchJson(`${API}/mappings/batch`, { method: "POST", body: JSON.stringify(body) });
}

/** 적용기간 표시 — 종료 null 은 "무기한"(별도 종료 공지 전까지 유효) */
/**
 * 적용기간 한 줄 표기 — 액면·구두가 **이 한 함수를 함께 쓴다**(두 갈래가 갈라지지 않게).
 *
 * 시각을 주면 날짜 뒤에 붙여 `2026-08-15 13:00~18:00` 처럼 찍는다(티켓 #516).
 * 시각이 없으면 종전과 글자 하나 다르지 않다 — 이미 올려둔 정책 표기는 그대로다.
 */
export function formatPeriod(
  from: string | null,
  to: string | null,
  fromTime?: string | null,
  toTime?: string | null,
): string {
  if (!from && !to) return "기간 미지정";
  if (!fromTime && !toTime) {
    // 시각을 안 쓴 정책 — 종전 표기 그대로.
    if (from === to) return from!;
    return `${from ?? "?"} ~ ${to ?? "무기한"}`;
  }
  // 하루 안에서 끝나는 정책(종료일 미지정 포함)은 한 날짜에 시각 범위를 붙인다.
  if (to == null || to === from) return `${from ?? "?"} ${fromTime ?? ""}~${toTime ?? ""}`;
  return `${`${from ?? "?"} ${fromTime ?? ""}`.trim()} ~ ${`${to} ${toTime ?? ""}`.trim()}`;
}

/** 파일명 옆에 붙는 짧은 시각 꼬리표 — `13:00~`·`13:00~18:00`·`~18:00`. 시각이 없으면 빈 문자열. */
export function formatClockRange(
  fromTime: string | null | undefined,
  toTime: string | null | undefined,
): string {
  if (!fromTime && !toTime) return "";
  return `${fromTime ?? ""}~${toTime ?? ""}`;
}

/** KST 기준 오늘 YYYY-MM-DD */
export function todayKst(): string {
  return new Intl.DateTimeFormat("sv-SE", { timeZone: "Asia/Seoul" }).format(new Date());
}

// ───────────────────── 이상탐지 1단계 관측 (시계열 지표) ─────────────────────
// 같은 업체의 과거 업로드 대비 "평소와 다름"을 5종 신호로 기록 (server: baseline-metrics.ts).

export type PolicyMetrics = {
  calculatedAt: string;
  baselineCount: number; // 비교에 쓴 직전 업로드 수 (0 = 첫 업로드)
  rowCount: number;
  unmappedRatio: number;
  vanishedModels: Array<{ code: string; name: string | null; presence: number }>;
  newModels: Array<{ code: string; name: string | null }>;
  priceJumps: Array<{
    model: string;
    plan: string | null;
    dealType: string | null;
    before: number;
    after: number;
    pct: number;
  }>;
  rowCountShift: { baselineAvg: number; current: number; pct: number } | null;
  unmappedSurge: { baselineRatio: number; current: number; examples: string[] } | null;
  /** 한 업로드 안에서 다른 원문 줄이 같은 칸에 다른 금액(별칭이 넓게 잡힌 신호). 2026-09-19 전 계산분엔 없다 */
  crossRawConflicts?: {
    cellCount: number;
    groups: Array<{
      raws: Array<{ raw: string; cells: string[]; min: number; max: number }>;
      models: string[];
      cellCount: number;
      maxGap: number;
    }>;
  };
};
